import { CONTEXT_TYPES } from 'constants/menu';

const MAIN_PROVIDER = 'main';

// Mirrors orders.co-mms `buildMealMeExportPayload`: produces the `menu` body
// accepted by POST /menu/import (MMS /menu/meal-me/create) from the
// POST /menu/get-provider-menu response for the main provider. That response
// already resolves `price` / `pickupPrice` to the main provider and strips
// provider overrides from options.
//
// Every entity must carry a non-null, unique `oco_external_id` for the import
// to round-trip; entities created outside meal-me have none, so their native
// `_id` is used instead.

const toExternalId = (entity) => {
  if (entity.oco_external_id) return entity.oco_external_id;
  if (!entity._id) {
    throw new Error(`Menu entity "${entity.name}" has no _id to export`);
  }
  return String(entity._id);
};

const resolveMainPrice = (priceOverride, basePrice, basePickupPrice) => {
  const main = (priceOverride || []).find(
    (po) =>
      po?.context_type === CONTEXT_TYPES.provider &&
      po?.context_value === MAIN_PROVIDER
  );
  const price = main?.price ?? basePrice ?? 0;
  return { price, pickupPrice: main?.pickupPrice ?? basePickupPrice ?? price };
};

const mapOptionSizeOverrides = (priceOverride = [], sizeOptionIdToExternal) =>
  (priceOverride || [])
    .filter(
      (po) =>
        po?.context_type === CONTEXT_TYPES.modifierItem &&
        sizeOptionIdToExternal[String(po.context_value)]
    )
    .map((po) => ({
      context_type: CONTEXT_TYPES.modifierItem,
      context_value: sizeOptionIdToExternal[String(po.context_value)],
      price: po.price ?? 0,
      pickupPrice: po.pickupPrice ?? po.price ?? 0,
    }));

const mapModifierGroup = (
  group,
  includeSizeOverrides,
  sizeOptionIdToExternal
) => ({
  oco_external_id: toExternalId(group),
  name: group.name,
  sort: group.sort ?? 0,
  min: group.min ?? null,
  max: group.max ?? null,
  list: [...(group.list || [])]
    .sort((a, b) => (a.optionSort ?? 0) - (b.optionSort ?? 0))
    .map((option) => {
      const { price, pickupPrice } = resolveMainPrice(
        option.priceOverride,
        option.price,
        option.pickupPrice
      );
      const mapped = {
        oco_external_id: toExternalId(option),
        name: option.name,
        price,
        pickupPrice,
        sort: option.optionSort ?? option.sort ?? 0,
      };
      if (includeSizeOverrides) {
        const overrides = mapOptionSizeOverrides(
          option.priceOverride,
          sizeOptionIdToExternal
        );
        if (overrides.length) {
          mapped.priceOverride = overrides;
        }
      }
      return mapped;
    }),
});

const mapProduct = (product) => {
  const sizeTypeModifiers = product.sizeTypeModifiers || [];
  const sizeOptionIdToExternal = {};
  sizeTypeModifiers.forEach((sizeGroup) => {
    (sizeGroup.list || []).forEach((sizeOption) => {
      sizeOptionIdToExternal[String(sizeOption._id)] =
        toExternalId(sizeOption);
    });
  });

  const { price } = resolveMainPrice(product.priceOverride, product.price);

  return {
    oco_external_id: toExternalId(product),
    name: product.name,
    description: product.description ?? '',
    price,
    imageUrl: product.image?.url ?? null,
    isOnePrice: product.isOnePrice ?? true,
    tags: product.tags || [],
    sizeTypeModifiers: sizeTypeModifiers.map((group) =>
      mapModifierGroup(group, false, sizeOptionIdToExternal)
    ),
    modifiers: [...(product.modifiers || [])]
      .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
      .map((group) => mapModifierGroup(group, true, sizeOptionIdToExternal)),
  };
};

/**
 * @param {object} menu  POST /menu/get-provider-menu response for the main provider
 */
export const buildMenuExport = (menu) => ({
  oco_external_id: toExternalId(menu),
  categories: (menu.categories || []).map((category) => ({
    oco_external_id: toExternalId(category),
    name: category.name,
    description: category.description ?? '',
    sort: category.sort ?? 0,
    products: (category.products || []).map(mapProduct),
  })),
});
